import React, { MouseEvent } from 'react';
import styled from 'styled-components';

import OnGoing from '@/assets/lottie/loading-dot-ongoing.json';
import Wait from '@/assets/lottie/loading-dot-wait.json';
import CustomLottieIcon from '@/components/atom/CustomLottieIcon';
import MaterialIcon from '@/components/atom/MaterialIcon';
import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import AlertInformationCircle from '@/components/atom/icon/AlertInformationCircle';
import DotMenu from '@/components/atom/icon/DotMenu';
import FavoriteStar from '@/components/atom/icon/FavoriteStar';
import Share from '@/components/atom/icon/Share';
import Lock from '@/components/atom/icon/qna/Lock';
import { SelectableText } from '@/components/atom/text/DefaultText';
import ShareSelectModal, { KAKAO_SHARE_TYPE } from '@/components/molecules/Modal/SelectModal/ShareSelectModal';
import BlockQuestion from '@/components/molecules/QuestionList/QuestionItem/BlockQuestion';
import { QuestionDetailModalDataType } from '@/hooks/qna/useQuestionDetailViewModel';
import { REPORT_TYPE, ROLE_TYPE } from '@/hooks/useReportModal';
import theme from '@/styles/theme';
import { User } from '@/types/Auth';
import {
  AttachmentBlindType,
  QuestionAccessibleType,
  QuestionAnsweredStatus,
  QuestionStatus,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { QnaDetailVo } from '@/types/qna';
import { absoluteTime, formatShareUrl, getQnaAnswerStatusInDetail } from '@/utils/format';

import QuestionAttachments, { QuestionAttachmentsProps } from './QuestionAttachments';

interface Props extends Omit<QuestionAttachmentsProps, 'attachments'> {
  questionDetail: QnaDetailVo;
  qnaAttachments: QnaDetailVo['attachments'];
  imageZoomIndex: number;
  pageName: string;
  onShareModal: boolean;
  user: User;
  isMyQuestion: boolean;
  onShowModal: (e: MouseEvent<HTMLButtonElement>, data: QuestionDetailModalDataType) => void;
  onClickImage: (index: number) => void;
  onCloseImageZoom: () => void;
  onClickLikeQuestion: (like: boolean) => void;
  onChangeShowShareModal: () => void;
}

const QuestionDetail = ({
  questionDetail,
  qnaAttachments,
  imageZoomIndex,
  pageName,
  onShareModal,
  imageSlideIdx,
  isMyQuestion,
  onShowModal,
  onClickImage,
  onCloseImageZoom,
  onClickLikeQuestion,
  onChangeShowShareModal,
  onChangeImageBlindType,
  onChangeSlideImage,
}: Props) => {
  const isPrivate = Boolean(questionDetail.accessibleType === QuestionAccessibleType.Me);

  return (
    <>
      <Wrapper>
        <Question>
          {questionDetail.isBlockedByMe ? (
            <BlockQuestion />
          ) : (
            <QuestionTop>
              <QuestionTitle>{questionDetail.title}</QuestionTitle>
              <QuestionWriter>
                <div>{questionDetail.nameAndDate}</div>
                <time dateTime={absoluteTime(questionDetail.createdAt)} />
                {isPrivate && (
                  <HiddenQuestionLabel>
                    <Lock />
                    비공개
                  </HiddenQuestionLabel>
                )}
              </QuestionWriter>

              <SelectableText>{questionDetail.content}</SelectableText>
              {/* 첨부 이미지 */}
              {!!qnaAttachments.length && (
                <QuestionAttachments
                  questionDetail={questionDetail}
                  onClickImage={onClickImage}
                  imageZoomIndex={imageZoomIndex}
                  onCloseImageZoom={onCloseImageZoom}
                  attachments={qnaAttachments}
                  imageSlideIdx={imageSlideIdx}
                  onChangeImageBlindType={onChangeImageBlindType}
                  onChangeSlideImage={onChangeSlideImage}
                />
              )}
              {/* 비공개 사진이 있는 내 글의 경우 배너 */}
              {questionDetail.isMine &&
                questionDetail.attachments.some(
                  (attachment) => attachment.blindType === AttachmentBlindType.Blinded
                ) && (
                  <QuestionViewOffBanner>
                    <AlertInformationCircle size={12} color={theme.color.gray500} />
                    의료진 검수 전이거나 보호가 필요한 사진이에요.
                  </QuestionViewOffBanner>
                )}
              <Tags>
                {!questionDetail.tags.length ? (
                  <Tag>{questionDetail.category.name}</Tag>
                ) : (
                  questionDetail.tags.map((tag) => <Tag key={tag.name}>{tag.name}</Tag>)
                )}
              </Tags>
            </QuestionTop>
          )}

          {/* 보관/공유/신고 */}
          <QuestionsBottom>
            <QuestionsBottomButtons>
              <QuestionBottomButton
                activeFont={questionDetail.isLikedByMe}
                onClick={() => onClickLikeQuestion(!questionDetail.isLikedByMe)}
              >
                <FavoriteStar fullColor={theme.color.primary500} empty={!questionDetail.isLikedByMe} />
                보관
              </QuestionBottomButton>
              <QuestionBottomButton onClick={onChangeShowShareModal}>
                <Share /> 공유
              </QuestionBottomButton>
            </QuestionsBottomButtons>

            {!isMyQuestion ? (
              <ReportButton
                type='button'
                onClick={(e) =>
                  onShowModal(e, {
                    report: {
                      targetId: questionDetail.questionPid,
                      targetType: REPORT_TYPE.QUESTION,
                      roleId: questionDetail.user.userId,
                      role: ROLE_TYPE.USER,
                      isBlocked: questionDetail.isBlockedByMe,
                    },
                  })
                }
              >
                <DotMenu />
              </ReportButton>
            ) : (
              <PrivateButton
                type='button'
                onClick={(e) =>
                  onShowModal(e, {
                    private: {},
                  })
                }
              >
                <DotMenu />
              </PrivateButton>
            )}
          </QuestionsBottom>

          {/* 답변 달리지 않은 경우 배너 노출 */}
          {questionDetail.isNotAnswered && questionDetail.status !== QuestionStatus.AwaitingBasicAnswer && (
            <QuestionAnswerStatusBanner
              isAnswering={questionDetail.answeredStatus === QuestionAnsweredStatus.Answering}
            >
              <CustomLottieIcon
                width={24}
                height={24}
                animationData={questionDetail.answeredStatus === QuestionAnsweredStatus.Answering ? OnGoing : Wait}
                style={{ marginRight: 12 }}
              />
              {getQnaAnswerStatusInDetail(questionDetail.answeredStatus)}
            </QuestionAnswerStatusBanner>
          )}
        </Question>

        {/* 공유하기 카카오톡/주소복사 */}
        <ShareSelectModal
          isVisible={onShareModal}
          onClose={onChangeShowShareModal}
          type={KAKAO_SHARE_TYPE.QNA}
          pageName={pageName}
          kakaoShare={{
            id: questionDetail.questionPid,
            title: questionDetail.title,
            content: questionDetail.content,
          }}
          copyLink={{
            id: questionDetail.questionPid,
            copyElementId: 'copyQna',
            copyLinkUrl: `${formatShareUrl(ROUTES.QNA.HOME.path)}/${questionDetail.questionPid}`,
          }}
        />

        <FootNote>
          <MaterialIcon name={'error'} color={theme.color.gray400} size={16} />
          실시간 의료상담의 모든 게시물은 저작권의 보호를 받아요. 저작권자의 명시적 동의 없이 게시물을 복제, 배포, 전송
          등 활용하는 것은 저작권 침해로서 법적 책임을 질 수 있으니 유의하세요!
        </FootNote>
      </Wrapper>
    </>
  );
};

export default QuestionDetail;

const Wrapper = styled.section`
  padding: 24px 24px 32px;

  ${({ theme }) => `
    background: ${theme.color.white};
  `}
`;

const ReportButton = styled(DefaultButtonStyled)``;

const PrivateButton = styled(DefaultButtonStyled)``;

const Question = styled.article`
  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray600};
    
  `}
  white-space: pre-wrap;
`;

const QuestionTop = styled.div``;
const QuestionTitle = styled.h1`
  ${({ theme }) => `
   ${theme.typography.UI24SB};
   color: ${theme.color.gray800};
  `}
`;

const QuestionWriter = styled.div`
  margin: 8px 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ theme }) => `
  ${theme.typography.UI14R};
  color: ${theme.color.gray500};
  `};

  time {
    visibility: hidden;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 20px;
`;

const Tag = styled.span`
  padding: 3px 6px;
  border-radius: 4px;

  ${({ theme }) => `
    ${theme.typography.UI12M};
    color: ${theme.color.gray400};
    background: ${theme.color.gray50};
  `}
`;

const QuestionsBottom = styled.div`
  display: flex;
  margin-top: 24px;
  justify-content: space-between;
`;

const QuestionsBottomButtons = styled.div`
  display: flex;

  button + button {
    margin-left: 8px;
  }
`;

const QuestionBottomButton = styled(DefaultButtonStyled)<{
  activeFont?: boolean;
}>`
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 10px 16px;

  ${({ theme, activeFont }) => `
    ${theme.typography.UI14M};
    color: ${activeFont ? theme.color.primary500 : theme.color.gray500};
    border: 1px solid ${theme.color.gray200};

    &:active {
      background: ${theme.color.gray50};
    };
  `}

  svg {
    margin-right: 8px;
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  margin-top: 24px;
`;

const QuestionAnswerStatusBanner = styled(Banner)<{
  isAnswering: boolean;
}>`
  padding: 20px 24px;

  ${({ theme, isAnswering }) => `
    ${theme.typography.UI14SB};
    color: ${isAnswering ? theme.color.primary500 : theme.color.blue500};
    background: ${theme.color.gray40};
  `};
`;

const QuestionViewOffBanner = styled.div`
  padding: 15px 16px;
  margin-top: 20px;
  display: inline-flex;
  align-items: center;

  svg {
    margin-right: 6px;
  }

  ${({ theme }) => `
    ${theme.typography.UI12M};
    color: ${theme.color.gray500};
    background: ${theme.color.gray40};
    border-radius: 8px;
  `};
`;

const FootNote = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 24px;

  ${({ theme }) => `
    ${theme.typography.UI12R};
    color: ${theme.color.gray400};
  `};
  gap: 6px;
  span {
    margin: 1px 0;
  }
`;

const HiddenQuestionLabel = styled.div`
  display: flex;
  height: 28px;
  padding: 0 8px;
  align-items: center;
  gap: 4px;

  ${({ theme }) => `
    background: ${theme.color.red100};
    ${theme.typography.UI12M};
    color: ${theme.color.red600};
  `}
`;
